// Content Moderator → Events.
//
// ## The queue, in the order it is worked
//
//   New           a club's event nobody has looked at yet            → Approve / Deny
//   Needs review  sent back and changed since, or edited by the club
//                 after it was approved                             → Approve / Deny, or "Looks fine"
//   Listed        approved and on the map, not finished yet          → the status switch
//   Off the map   unlisted, disabled or denied, not finished yet
//   Finished      over, by the site's own rule (no end = two hours)
//
// The screen opens on the first of those with anything in it, so the thing a
// volunteer sees on arrival is the work.
//
// ## A row answers "is this OK?" without opening anything
//
// The club's LOGO (resolved server-side from the club the event is BY — never
// the operator's, see api/event-review.js), the name, the club, when, where,
// the tags, and what is true right now. "Details" opens the rest: the club's
// own page body exactly as an attendee would see it, the history of what was
// decided and sent, and a box to email the club.
//
// ## ⚠️ Deny ALWAYS asks why
//
// The reason is emailed to the club and shown on its dashboard, verbatim, so a
// denial with no reason is a club told "no" with nothing to fix. Quick reasons
// fill the box rather than replacing it, so a volunteer can add the specific
// ("room 201 is a closet — did you mean 210?").

import { useCallback, useEffect, useMemo, useState } from 'react';
import EventBody from '../components/EventBody.jsx';
import { eventTags, eventWhere, ticketOf } from '../utils/eventTags.js';
import { eventUrl } from '../utils/eventSlug.js';
import { isFinished, fmtWhenShort } from '../utils/eventTime.js';
import { api, ago } from './shared.js';

const FILTERS = [
  { id: 'new', label: 'New' },
  { id: 'needs', label: 'Needs review' },
  { id: 'listed', label: 'Listed' },
  { id: 'off', label: 'Off the map' },
  { id: 'finished', label: 'Finished' },
  { id: 'all', label: 'All' },
];

const STATUS = {
  listed: { label: 'Listed', hint: 'On the map and in the app, and the link works.' },
  unlisted: { label: 'Unlisted', hint: 'Off the map, the app and every listing — but the link and QR still work.' },
  disabled: { label: 'Disabled', hint: 'The link and the QR stop working. Nothing is deleted.' },
};
const STATUS_ORDER = ['listed', 'unlisted', 'disabled'];

const QUICK_REASONS = [
  'The location or room looks wrong.',
  'The date or time is missing or looks wrong.',
  "The description doesn't say what the event is.",
  "This doesn't look like a student group event.",
  'This looks like a duplicate of another event.',
  'The event page has content we can’t put on the map.',
];

function bucketOf(e, now) {
  if (isFinished(e, now)) return 'finished';
  if (e.review === 'pending') return e.resubmittedAt ? 'needs' : 'new';
  if (e.review === 'approved' && e.changedSinceReview) return 'needs';
  if (e.review === 'denied' || e.status !== 'listed') return 'off';
  return 'listed';
}

/** What is true about this event right now, in words. */
function truthOf(e) {
  if (e.review === 'pending') return { cls: 'vt-pill-new', text: e.resubmittedAt ? 'Resubmitted' : 'New' };
  if (e.review === 'denied') return { cls: 'vt-pill-bad', text: 'Denied' };
  if (e.status === 'disabled') return { cls: 'vt-pill-bad', text: 'Disabled' };
  if (e.status === 'unlisted') return { cls: 'vt-pill-dim', text: 'Unlisted' };
  if (!e.clubVerified) return { cls: 'vt-pill-warn', text: 'Held — club not verified' };
  return { cls: 'vt-pill-good', text: 'Live' };
}

const startMs = (e) => {
  const t = Date.parse(`${e.date || ''}T${e.time || '00:00'}`);
  return Number.isFinite(t) ? t : Infinity;
};

export default function EventModeration({ token, onExpired, onCount }) {
  const [events, setEvents] = useState(null);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [filter, setFilter] = useState(null);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState('');
  const [details, setDetails] = useState(null);   // event id
  const [denying, setDenying] = useState(null);   // event id

  const load = useCallback(async () => {
    const r = await api('/api/event-review', { token, onExpired });
    if (!r.ok) { setErr(r.body.error || 'Could not load events.'); return; }
    setErr('');
    setEvents(r.body.events || []);
  }, [token, onExpired]);
  useEffect(() => { load(); }, [load]);

  const now = Date.now();
  const buckets = useMemo(() => {
    const b = { new: [], needs: [], listed: [], off: [], finished: [], all: [] };
    for (const e of events || []) { b[bucketOf(e, now)].push(e); b.all.push(e); }
    // The two queues are worked oldest-submitted first (fair to the club that
    // has waited longest); the rest in the order an attendee meets them.
    const bySubmitted = (a, z) => (a.resubmittedAt || a.changedSinceReview || a.createdAt || 0)
      - (z.resubmittedAt || z.changedSinceReview || z.createdAt || 0);
    b.new.sort(bySubmitted); b.needs.sort(bySubmitted);
    b.listed.sort((a, z) => startMs(a) - startMs(z));
    b.off.sort((a, z) => startMs(a) - startMs(z));
    b.finished.sort((a, z) => startMs(z) - startMs(a));
    b.all.sort((a, z) => startMs(z) - startMs(a));
    return b;
  }, [events, now]);

  useEffect(() => { if (events) onCount?.(buckets.new.length + buckets.needs.length); }, [events, buckets, onCount]);
  // Open on the work.
  useEffect(() => {
    if (!events || filter) return;
    setFilter(buckets.new.length ? 'new' : buckets.needs.length ? 'needs' : 'listed');
  }, [events, buckets, filter]);

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (buckets[filter || 'new'] || []).filter((e) => !needle
      || `${e.name} ${e.clubName} ${eventWhere(e)}`.toLowerCase().includes(needle));
  }, [buckets, filter, q]);

  const act = useCallback(async (id, body, done) => {
    setBusy(id); setMsg(''); setErr('');
    const r = await api('/api/event-review', { method: 'POST', token, onExpired, body: { id, ...body } });
    setBusy('');
    if (!r.ok) { setErr(r.body.error || 'That did not go through.'); return null; }
    if (done) setMsg(typeof done === 'function' ? done(r.body) : done);
    setEvents((list) => (list || []).map((e) => (e.id === id ? { ...e, ...r.body.event, clubLogo: e.clubLogo, clubName: e.clubName, clubVerified: e.clubVerified, canEmail: e.canEmail } : e)));
    return r.body;
  }, [token, onExpired]);

  // The club is emailed on approval, at most once per event a day and a few
  // times an hour per club (api/event-review.js) — say which happened.
  const approve = (e) => act(e.id, { op: 'approve' }, (b) => `Approved “${e.name}” — it’s listed now.${
    b.emailed ? ' The club was emailed.' : b.emailError ? ` ${b.emailError}` : ''}`);
  const ack = (e) => act(e.id, { op: 'ack' }, `Marked “${e.name}” as checked.`);
  const setStatus = (e, status) => act(e.id, { op: 'status', status }, `“${e.name}” is ${STATUS[status].label.toLowerCase()}.`);

  const open = details ? (events || []).find((e) => e.id === details) : null;
  const denyTarget = denying ? (events || []).find((e) => e.id === denying) : null;

  if (!events) return <div className="vt-empty">{err || 'Loading events…'}</div>;

  return (
    <>
      <div className="vt-tabs" role="group" aria-label="Filter events">
        {FILTERS.map((f) => (
          <button key={f.id} className="vt-tab" aria-pressed={filter === f.id} onClick={() => setFilter(f.id)}>
            {f.label}
            <span className={`vt-count${(f.id === 'new' || f.id === 'needs') && buckets[f.id].length ? ' vt-count-hot' : ''}`}>
              {buckets[f.id].length}
            </span>
          </button>
        ))}
      </div>
      <div className="vt-toolbar">
        <input className="vt-input" placeholder="Search by event, club or building" value={q} onChange={(e) => setQ(e.target.value)} />
        <button className="vt-btn" onClick={load}>Refresh</button>
      </div>
      {err && <div className="vt-err">{err}</div>}
      {msg && <div className="vt-ok">{msg}</div>}

      {!rows.length ? (
        <div className="vt-empty">
          {q ? 'No event matches that.' : filter === 'new' ? 'No new events waiting. 🎉'
            : filter === 'needs' ? 'Nothing needs a second look.' : 'Nothing here.'}
        </div>
      ) : (
        <div className="vt-list">
          {rows.map((e) => (
            <EventRow key={e.id} e={e} busy={busy === e.id}
                      onApprove={() => approve(e)} onDeny={() => setDenying(e.id)} onAck={() => ack(e)}
                      onStatus={(s) => setStatus(e, s)} onDetails={() => setDetails(e.id)} />
          ))}
        </div>
      )}

      {open && (
        <DetailsModal e={open} busy={busy === open.id} onClose={() => setDetails(null)}
                      onApprove={() => approve(open)} onDeny={() => { setDetails(null); setDenying(open.id); }}
                      onNotify={(message) => act(open.id, { op: 'notify', message },
                        (b) => (b.emailed ? `Emailed ${open.clubName}.` : `Not sent: ${b.emailError}`))} />
      )}
      {denyTarget && (
        <DenyModal e={denyTarget} busy={busy === denyTarget.id} onClose={() => setDenying(null)}
                   onDeny={async (reason, change, notify) => {
                     const b = await act(denyTarget.id, { op: 'deny', reason, change, notify },
                       (res) => (notify === false ? `Denied “${denyTarget.name}”. The club was not emailed.`
                         : res.emailed ? `Denied “${denyTarget.name}” and emailed ${denyTarget.clubName} what to change.`
                           : `Denied “${denyTarget.name}”, but the email did not send: ${res.emailError}`));
                     if (b) setDenying(null);
                   }} />
      )}
    </>
  );
}

function Logo({ e, size = 44 }) {
  return e.clubLogo
    ? <img className="vt-logo" src={e.clubLogo} alt="" style={{ width: size, height: size }} />
    : <span className="vt-logoNone" aria-hidden="true" style={{ width: size, height: size }}>🎪</span>;
}

function TagsLine({ e }) {
  const parts = eventTags(e).map((t) => t.label);
  const t = ticketOf(e);
  if (t) parts.push('Tickets');
  if (!parts.length) return null;
  return <div className="vt-meta">{parts.join(' · ')}</div>;
}

function EventRow({ e, busy, onApprove, onDeny, onAck, onStatus, onDetails }) {
  const truth = truthOf(e);
  const pending = e.review === 'pending';
  return (
    <div className={`vt-row${pending ? ' vt-row-hl' : ''}`}>
      <div className="vt-rowhead">
        <Logo e={e} />
        <div className="vt-grow">
          <div className="vt-title">{e.name || 'Untitled event'} <span className={`vt-pill ${truth.cls}`}>{truth.text}</span></div>
          <div className="vt-meta"><b>{e.clubName}</b> · {fmtWhenShort(e)}{eventWhere(e) ? ` · ${eventWhere(e)}` : ' · No location'}</div>
          <TagsLine e={e} />
          {e.changedSinceReview && e.review === 'approved' && (
            <div className="vt-meta" style={{ color: 'var(--warn)' }}>Edited by the club {ago(e.changedSinceReview)} — still listed.</div>
          )}
          {e.resubmittedAt && pending && (
            <div className="vt-meta" style={{ color: 'var(--warn)' }}>
              Resubmitted {ago(e.resubmittedAt)}. Last reason: “{e.denyReason}”
            </div>
          )}
          {pending && !e.clubVerified && (
            <div className="vt-meta">This club isn’t verified yet, so even once approved it stays off the map until UMaps verifies the club.</div>
          )}
          {pending && !e.resubmittedAt && <div className="vt-meta">Submitted {ago(e.createdAt)}</div>}
        </div>
        <div className="vt-actions">
          {pending ? (
            <>
              <button className="vt-btn vt-good" disabled={busy} onClick={onApprove}>Approve</button>
              <button className="vt-btn vt-danger" disabled={busy} onClick={onDeny}>Deny…</button>
            </>
          ) : e.review === 'denied' ? (
            <button className="vt-btn" disabled={busy} onClick={onApprove}>Approve anyway</button>
          ) : (
            <>
              {e.changedSinceReview && <button className="vt-btn vt-good" disabled={busy} onClick={onAck}>Looks fine</button>}
              <div className="vt-seg" role="group" aria-label="Event status">
                {STATUS_ORDER.map((k) => (
                  <button key={k} title={STATUS[k].hint} aria-pressed={e.status === k} disabled={busy || e.status === k}
                          onClick={() => onStatus(k)}>{STATUS[k].label}</button>
                ))}
              </div>
            </>
          )}
          <button className="vt-btn" onClick={onDetails}>Details</button>
        </div>
      </div>
    </div>
  );
}

function Modal({ children, onClose, label }) {
  useEffect(() => {
    const onKey = (ev) => { if (ev.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="vt-scrim" onMouseDown={(ev) => { if (ev.target === ev.currentTarget) onClose(); }}>
      <div className="vt-modal" role="dialog" aria-modal="true" aria-label={label}>{children}</div>
    </div>
  );
}

function DetailsModal({ e, busy, onClose, onApprove, onDeny, onNotify }) {
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const truth = truthOf(e);
  const t = ticketOf(e);
  const isPublic = e.review === 'approved' && e.status !== 'disabled' && e.clubVerified;
  const fmtDate = (ms) => (ms ? new Date(ms).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : '—');

  return (
    <Modal onClose={onClose} label={`Details: ${e.name}`}>
      <div className="vt-modalhead">
        <Logo e={e} size={52} />
        <div className="vt-grow">
          <div className="vt-title" style={{ fontSize: 17 }}>{e.name || 'Untitled event'}</div>
          <div className="vt-meta">{e.clubName} <span className={`vt-pill ${truth.cls}`}>{truth.text}</span></div>
        </div>
        <button className="vt-btn vt-ghost" onClick={onClose} aria-label="Close">✕</button>
      </div>
      <div className="vt-modalbody">
        <dl className="vt-facts">
          <dt>When</dt><dd>{fmtWhenShort(e)}{e.endTime ? '' : ' (no end time — assumed two hours)'}</dd>
          <dt>Where</dt><dd>{eventWhere(e) || <i>No location chosen</i>}</dd>
          {e.offCampus?.address && <><dt>Address</dt><dd>{e.offCampus.address}</dd></>}
          <dt>Tags</dt><dd>{eventTags(e).map((x) => x.label).join(' · ') || '—'}</dd>
          {t && <><dt>Tickets</dt><dd><a href={t.url} target="_blank" rel="noopener noreferrer">{t.label}</a> — check the link goes where it says.</dd></>}
          <dt>Club</dt><dd>{e.clubName}{e.clubVerified ? '' : ' (not verified yet)'}{e.canEmail ? '' : ' · no email on file'}</dd>
          <dt>Submitted</dt><dd>{fmtDate(e.createdAt)}{e.updatedAt && e.updatedAt !== e.createdAt ? ` · last edited ${fmtDate(e.updatedAt)}` : ''}</dd>
          {e.denyReason && <><dt>Last reason</dt><dd>{e.denyReason}{e.denyChange ? ` — ${e.denyChange}` : ''}</dd></>}
        </dl>

        <div className="vt-label">The event page</div>
        <div className="vt-card vt-pad" style={{ marginBottom: 14 }}>
          {(e.blocks || []).length ? <EventBody blocks={e.blocks} /> : <span className="vt-meta">The club hasn’t written anything on the page.</span>}
        </div>
        {isPublic && (
          <p className="vt-meta" style={{ marginTop: 0 }}>
            <a href={eventUrl(e)} target="_blank" rel="noreferrer">Open what attendees see ↗</a>
          </p>
        )}

        <div className="vt-field">
          <label className="vt-label" htmlFor="vt-notify">Email the club</label>
          <textarea id="vt-notify" className="vt-textarea" rows={3} value={note} onChange={(ev) => setNote(ev.target.value)}
                    placeholder="e.g. “Hi, you’ve selected the wrong room — did you mean EITC E2 110?”" />
          <div className="vt-hint">
            {e.canEmail
              ? 'Sent to the club’s own inbox from UMaps. Replies come back to the UMaps contact address. Nothing about the event changes.'
              : 'This club has no email address on file, so a message cannot be sent.'}
          </div>
          <button className="vt-btn vt-primary vt-small" style={{ marginTop: 8 }}
                  disabled={!note.trim() || sending || !e.canEmail}
                  onClick={async () => { setSending(true); const b = await onNotify(note.trim()); setSending(false); if (b) setNote(''); }}>
            {sending ? 'Sending…' : 'Send email'}
          </button>
        </div>

        {(e.reviewLog || []).length > 0 && (
          <>
            <div className="vt-label">History</div>
            <ul className="vt-log">
              {e.reviewLog.map((l, i) => (
                <li key={i}>{fmtDate(l.at)} · <b>{l.action}</b>{l.by ? ` by ${l.by}` : ''}{l.note ? ` — “${l.note}”` : ''}</li>
              ))}
            </ul>
          </>
        )}
      </div>
      <div className="vt-modalfoot">
        {e.review !== 'approved' && <button className="vt-btn vt-good" disabled={busy} onClick={onApprove}>Approve</button>}
        {e.review === 'pending' && <button className="vt-btn vt-danger" disabled={busy} onClick={onDeny}>Deny…</button>}
        <button className="vt-btn" onClick={onClose}>Close</button>
      </div>
    </Modal>
  );
}

function DenyModal({ e, busy, onClose, onDeny }) {
  const [reason, setReason] = useState('');
  const [change, setChange] = useState('');
  const [notify, setNotify] = useState(!!e.canEmail);
  const add = (r) => setReason((cur) => (cur.includes(r) ? cur : (cur.trim() ? `${cur.trim()} ${r}` : r)));
  return (
    <Modal onClose={onClose} label={`Deny ${e.name}`}>
      <div className="vt-modalhead">
        <Logo e={e} />
        <div className="vt-grow">
          <div className="vt-title">Deny “{e.name}”</div>
          <div className="vt-meta">It comes off the map and the club is asked to fix it. Saving it again sends it back to you.</div>
        </div>
      </div>
      <div className="vt-modalbody">
        <div className="vt-field">
          <label className="vt-label" htmlFor="vt-reason">Why it wasn’t approved</label>
          <div className="vt-chips">
            {QUICK_REASONS.map((r) => <button key={r} type="button" className="vt-chip" onClick={() => add(r)}>{r}</button>)}
          </div>
          <textarea id="vt-reason" className="vt-textarea" value={reason} onChange={(ev) => setReason(ev.target.value)} autoFocus />
        </div>
        <div className="vt-field">
          <label className="vt-label" htmlFor="vt-change">What they need to change</label>
          <textarea id="vt-change" className="vt-textarea" value={change} onChange={(ev) => setChange(ev.target.value)}
                    placeholder="Be specific — this is what they’ll act on." />
        </div>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input type="checkbox" checked={notify} disabled={!e.canEmail} onChange={(ev) => setNotify(ev.target.checked)} />
          {e.canEmail ? `Email ${e.clubName} the reason and what to change` : 'This club has no email on file — it will see the reason on its dashboard'}
        </label>
      </div>
      <div className="vt-modalfoot">
        <button className="vt-btn" onClick={onClose}>Cancel</button>
        <button className="vt-btn vt-dangerSolid" disabled={!reason.trim() || busy}
                onClick={() => onDeny(reason.trim(), change.trim(), notify)}>
          {busy ? 'Denying…' : notify ? 'Deny and email the club' : 'Deny'}
        </button>
      </div>
    </Modal>
  );
}
